import React, { useCallback, useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import { useMemo } from 'react';
import claimGroupService from '../../services/claim_group.service';
import businessUnitsService from '../../services/business_unit.service';
import ReviewableFieldEditor from '../../util/textEdit/ReviewableFieldEditor';
import { handleError } from '../../store/features/alertSlice';
import { Loader } from '../../util/Loader';
import { getDefaultConfig } from '../../util/tinyMceConfig';
import { Modal } from '../../util';
export default function EditBusinessUnitOverviewModal(_ref) {
  let {
    businessUnitId,
    companyId,
    onHide,
    onContentChanged,
    isVisible,
    title,
    claimGroupId = null,
    isUnderReview = false,
    footnoteStartIndex = 0
  } = _ref;
  const [editText, setEditText] = useState({});
  const [apiUpdateResult, setApiUpdateResult] = useState({});
  const [highlightsText, setHighlightsText] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [closeButtonLoading, setCloseButtonLoading] = useState(false);
  const [reportTextWasChanged, setReportTextWasChanged] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const showTrackChangesControls = user.account_type !== 'customer';
  const [claimGroup, setClaimGroup] = claimGroupService.useGetClaimGroup(claimGroupId);
  const [businessUnit, setBusinessUnit] = businessUnitsService.useFetchBusinessUnit(companyId, businessUnitId, setLoading);
  const updateBusinessUnit = businessUnitsService.useUpdateBusinessUnit(companyId, setBusinessUnit);

  /**
   * 
   * @param {*} editText always provided
   * @param {*} highlightText always provided
   */

  const setEditData = useCallback(newEditText => {
    setEditText({
      content: newEditText,
      timeOfUpdate: null
    });
    setHighlightsText(newEditText);
  }, []);
  useEffect(() => {
    setEditData(businessUnit?.overview);
  }, [businessUnit, setEditData]);
  const debouncedUpdateReportText = useMemo(() => {
    setReportTextWasChanged(true);
    const updateBusinessUnitOverview = async (text, timeOfUpdate) => {
      await updateBusinessUnit({
        ...businessUnit,
        overview: text.content
      });
      setApiUpdateResult({
        editDataParam: businessUnit.overview,
        timeOfUpdate
      });
    };
    return debounce(async (text, timeOfUpdate) => {
      try {
        await updateBusinessUnitOverview(text, timeOfUpdate);
        setCloseButtonLoading(false);
      } catch (error) {
        console.log(error);
        dispatch(handleError(error));
      }
    }, 1000);
  }, [businessUnit, updateBusinessUnit, dispatch]);
  useEffect(() => {
    if (editText.timeOfUpdate) {
      if (editText.timeOfUpdate > apiUpdateResult.timeOfUpdate || !apiUpdateResult.timeOfUpdate) {
        debouncedUpdateReportText(editText, editText.timeOfUpdate);
      } else if (apiUpdateResult.timeOfUpdate && apiUpdateResult.timeOfUpdate >= editText.timeOfUpdate) {
        setEditData(apiUpdateResult.editDataParam);
      }
    }
  }, [apiUpdateResult, debouncedUpdateReportText, editText, setEditData]);
  const editBusinessUnitOverviewModalBody = () => {
    if (isLoading) {
      return /*#__PURE__*/React.createElement(Loader, null);
    }
    return /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Card.Body, null, claimGroup && user.account_type === 'customer' && claimGroup.active_change_set.finalized_at && /*#__PURE__*/React.createElement("div", {
      dangerouslySetInnerHTML: {
        __html: editText.content
      }
    }), claimGroup && !(user.account_type === 'customer' && claimGroup.active_change_set.finalized_at) && /*#__PURE__*/React.createElement(ReviewableFieldEditor, {
      isUnderReview: isUnderReview,
      editorContent: editText.content,
      modelClass: "BusinessUnit",
      modelId: businessUnitId,
      modelField: 'business_unit_overview',
      editorInitConfig: getDefaultConfig({
        footnoteStartIndex,
        stylesFormatterToggleIniitialValue: true,
        modules: {
          styles_formatter: true
        }
      }),
      trackChangesOnCreateComment: content => {
        setCloseButtonLoading(true);
        const timeOfUpdate = Date.now();
        setEditText({
          content,
          timeOfUpdate
        });
      },
      trackChangesEditoInitFn: addCommentCallback => {
        const editorModuleOptions = {
          isTrackingChanges: true,
          onAddCommentButtonClickCallback: addCommentCallback,
          showTrackChangesControls: showTrackChangesControls,
          footnoteStartIndex
        };
        return getDefaultConfig(editorModuleOptions);
      },
      onEditorChange: content => {
        setCloseButtonLoading(true);
        const timeOfUpdate = Date.now();
        setEditText({
          content,
          timeOfUpdate
        });
      },
      showTrackChangesControls: showTrackChangesControls
    })));
  };
  return /*#__PURE__*/React.createElement(Modal, {
    size: "xl",
    title: title || 'Edit text',
    show: isVisible,
    renderBody: editBusinessUnitOverviewModalBody,
    onHide: onContentChanged,
    closeButtonLoading: closeButtonLoading,
    disabled: closeButtonLoading,
    closeText: "Save"
  });
}